import type { BadgeTone } from '@/lib/utils/badgeTone'

import type {
  DashboardNoticeDispatchStatus,
  DashboardPriorityItemSeverity,
  VenueDashboardCourtsSummary,
  VenueDashboardPriorityItem,
  VenueDashboardSettlementSummary,
  VenueDashboardView,
} from './dashboardTypes'
import { EMPTY_MARK, formatAmount, formatDate, formatDateTime } from './format'
import type { ReservationRefundSummaryView } from './reservationRefundTypes'

/**
 * 대시보드 화면 전용 표현 매핑 (설계 §2-3). `if (status === ...)` 를 페이지·컴포넌트에 흩지 않는다.
 */

/** KST(Asia/Seoul) 기준 "이번 달"(`YYYY-MM`). 취소·환불 요약 EP 의 필수 쿼리 파라미터로 쓴다. */
export function currentYearMonthKst(now: Date = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Seoul',
    year: 'numeric',
    month: '2-digit',
  }).formatToParts(now)
  const year = parts.find((part) => part.type === 'year')?.value ?? ''
  const month = parts.find((part) => part.type === 'month')?.value ?? ''
  return `${year}-${month}`
}

export interface DashboardCourtsViewModel {
  valueText: string
  subText: string
}

/**
 * 🚨 Figma 의 "이용 중 코트" 문구를 따르지 않는다 — 분자는 예약 도메인이 있어야 계산되고, 이
 * 필드는 "운영 중 + 오늘 요일" 만 반영한다(휴무·기존 예약 미반영). 설계 §2-1(c) 자율 결정 3.
 */
export function toDashboardCourtsViewModel(courts: VenueDashboardCourtsSummary): DashboardCourtsViewModel {
  return {
    valueText: `${courts.reservable} / ${courts.total}`,
    subText: '운영 중 코트 기준 (휴무·기존 예약 미반영)',
  }
}

export interface DashboardCancellationStatViewModel {
  valueText: string
  badgeText: string | null
}

/** 취소 요청 통계 카드 — `#589` 전용 요약 EP 를 쓴다(대시보드 EP 의 `cancellations` 가 아니다). */
export function toDashboardCancellationStat(
  summary: ReservationRefundSummaryView | undefined
): DashboardCancellationStatViewModel {
  if (!summary) return { valueText: EMPTY_MARK, badgeText: null }
  return {
    valueText: `${summary.inProgressCount.toLocaleString('ko-KR')}건`,
    badgeText: summary.inProgressCount > 0 ? '처리 필요' : null,
  }
}

export interface DashboardRevenueStatViewModel {
  valueText: string
  subText: string
}

export function toDashboardRevenueStat(settlement: VenueDashboardSettlementSummary): DashboardRevenueStatViewModel {
  return {
    valueText: formatAmount(settlement.confirmedAmount),
    subText: `정산 예정 ${formatAmount(settlement.nextPayoutAmount)} · ${formatDate(settlement.nextPayoutDate)} 지급`,
  }
}

export interface DashboardSettlementViewModel {
  confirmedAmountText: string
  estimated: boolean
  nextPayoutText: string
  inProgressRefundText: string
}

export function toDashboardSettlementViewModel(
  settlement: VenueDashboardSettlementSummary
): DashboardSettlementViewModel {
  return {
    confirmedAmountText: formatAmount(settlement.confirmedAmount),
    estimated: settlement.estimated,
    nextPayoutText: `${formatAmount(settlement.nextPayoutAmount)} · ${formatDate(settlement.nextPayoutDate)} 지급 예정`,
    inProgressRefundText:
      settlement.inProgressRefundAmount > 0
        ? `처리 중 환불 차감 ${formatAmount(settlement.inProgressRefundAmount)}`
        : '처리 중인 환불 차감 없음',
  }
}

export interface DashboardNoticeItemViewModel {
  key: string
  title: string
  statusLabel: string
  tone: BadgeTone
  dateText: string
}

/** 🚨 `delivered`/`SENT` 축을 톤 매핑에만 쓴다 — 문자열 라벨은 서버가 만든 `statusLabel` 그대로 쓴다. */
const NOTICE_STATUS_TONE: Record<DashboardNoticeDispatchStatus, BadgeTone> = {
  SENT: 'success',
  SENDING: 'warning',
  QUEUED: 'neutral',
  FAILED: 'danger',
}

export function toDashboardNoticeItems(
  items: VenueDashboardView['noticeDispatches']
): DashboardNoticeItemViewModel[] {
  return items.map((item) => ({
    key: String(item.id),
    title: item.title,
    statusLabel: item.statusLabel,
    tone: NOTICE_STATUS_TONE[item.status] ?? 'neutral',
    // 🚨 미종결(`sentAt === null`)이면 요청 시각을 대신 그린다(#590 §4-2 가이드).
    dateText: formatDateTime(item.sentAt ?? item.requestedAt),
  }))
}

const PRIORITY_ITEM_SEVERITY_TONE: Record<DashboardPriorityItemSeverity, BadgeTone> = {
  INFO: 'neutral',
  WARNING: 'warning',
  CRITICAL: 'danger',
}

/**
 * `actionType`→이동 경로(`#780` §3 "actionType 이 화면 라우팅의 열쇠"). 🚫 forward-only — 모르는
 * `actionType`은 이 맵에 없으므로 비활성(non-interactive) 행으로 렌더된다(`#782` 설계 §2).
 */
const PRIORITY_ITEM_ACTION_PATH: Record<string, (venueId: string) => string> = {
  OPERATION_VERIFICATION: (venueId) => `/venues/${venueId}/operation-verification/expiry-notice`,
}

export interface DashboardPriorityItemViewModel {
  key: string
  label: string
  message: string
  tone: BadgeTone
  remainingDaysText: string | null
  path: string | null
}

/**
 * 대시보드 "우선 처리 항목"(`#780` §3, `#782`) — `message`는 서버가 만든 문구 그대로, `remainingDays`는
 * "D-N" 배지로만 보조 표시한다(🚫 임박 여부를 다시 계산하지 않는다 — 서버가 이미 판정해 배열에 담았다).
 */
export function toDashboardPriorityItemViewModels(
  items: VenueDashboardPriorityItem[],
  venueId: string
): DashboardPriorityItemViewModel[] {
  return items.map((item, index) => ({
    key: `${item.type}-${index}`,
    label: item.label,
    message: item.message,
    tone: PRIORITY_ITEM_SEVERITY_TONE[item.severity as DashboardPriorityItemSeverity] ?? 'neutral',
    remainingDaysText: item.remainingDays !== null ? `D-${item.remainingDays}` : null,
    path: PRIORITY_ITEM_ACTION_PATH[item.actionType]?.(venueId) ?? null,
  }))
}

// 🚨 "운영 인증" 카드는 `#778`부터 `OperationVerificationSummaryCard`(EP-A `badge` 5상태 기반)를
// 쓴다 — 대시보드 EP 내장 3필드(`verificationStatus` 축뿐, `UNDER_REVIEW`/`REJECTED` 구분 불가)
// 기반이던 `toDashboardOperationVerificationViewModel`은 더 이상 쓰지 않아 제거했다(설계 §2 자율 결정 3).
